/**
 * Alerts by location - the first thing on the page: per settlement, the people
 * exposed, when the flood arrives from the breach, how deep it gets, its hazard
 * class (the run's depth-velocity classification), how far the escalation goes
 * and which role designations carry it. Sortable by arrival.
 *
 * Only settlements the flood front actually reached appear. A settlement the
 * run never wetted has no arrival time, no depth and no lead time, so it is not
 * a row here with three blanks in it - it is in the advisory list underneath,
 * which is a different statement and says so in one line.
 */

import { useState } from 'react'

import { HAZARD, HAZARD_LABELS } from '../../components/Legend'
import { SEVERITY_COLOUR, hm } from './shared'
import type { DispatchEvent, SettlementRow } from './shared'

interface Props {
  rows: SettlementRow[]
  advisory: SettlementRow[]
  events: DispatchEvent[]
  selected: SettlementRow | null
  onSelect: (settlement: SettlementRow | null) => void
}

export function SettlementTable({ rows, advisory, events, selected, onSelect }: Props) {
  const hasPopulation = rows.some((s) => s.population !== null)
  const [arrivalDesc, setArrivalDesc] = useState(false)
  const sortedRows = [...rows].sort((a, b) =>
    arrivalDesc
      ? (b.arrival_time_min as number) - (a.arrival_time_min as number)
      : (a.arrival_time_min as number) - (b.arrival_time_min as number),
  )
  const forPlace = (id: number) => events.filter((e) => e.settlement_id === id)
  const topTier = (id: number) => Math.max(0, ...forPlace(id).map((e) => e.tier))
  const firstRoles = (id: number) => {
    const tier1 = forPlace(id).filter((e) => e.tier === 1)
    return [...new Set(tier1.map((e) => e.recipient))]
  }

  // The drill-down under the table: every dispatch sent on behalf of the one
  // settlement in view. With several in view the table alone is the summary.
  const detail =
    rows.length === 1
      ? events
          .filter((e) => e.settlement_id === rows[0].settlement_id)
          .sort((a, b) => a.t_notified_min - b.t_notified_min || a.tier - b.tier)
      : []

  return (
    <section className="panel" style={{ padding: 0 }} data-testid="settlement-alerts">
      <div className="px-4 pb-3 pt-2">
        <div className="overflow-x-auto">
        <table className="data-table w-full" style={{ minWidth: 1080 }}>
          <thead>
            <tr>
              <th>{hasPopulation ? 'place' : 'location'}</th>
              {hasPopulation && <th className="num">population exposed</th>}
              <th className="num" aria-sort={arrivalDesc ? 'descending' : 'ascending'}>
                <button type="button" onClick={() => setArrivalDesc((d) => !d)} className="uppercase" style={{ letterSpacing: 'inherit' }}>
                  arrival from breach {arrivalDesc ? '▾' : '▴'}
                </button>
              </th>
              <th className="num">peak depth m</th>
              <th>hazard class</th>
              <th>severity</th>
              <th className="num">escalation tier</th>
              <th>responsible</th>
              <th className="num">lead time</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((s) => {
              const active = selected?.settlement_id === s.settlement_id
              return (
                <tr
                  key={s.settlement_id}
                  onClick={() => onSelect(active ? null : s)}
                  style={{
                    cursor: 'pointer',
                    background: active ? 'var(--ink-surface-raised)' : undefined,
                  }}
                >
                  <td style={{ color: 'var(--ink-text)' }}>
                    {s.name}
                    <div style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}>
                      {[hasPopulation ? null : s.type, s.district, s.chainage_km !== null ? `ch ${s.chainage_km.toFixed(1)} km` : null].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  {hasPopulation && <td className="num">{(s.population ?? 0).toLocaleString('en-IN')}</td>}
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>{hm(s.arrival_time_min as number)}</td>
                  <td className="num">{s.max_depth_m.toFixed(2)}</td>
                  <td>
                    {s.hazard_class !== null && (
                      <span className="flex items-center gap-2">
                        <span style={{ width: 10, height: 10, background: HAZARD[s.hazard_class], display: 'inline-block' }} />
                        {s.hazard_class} · {HAZARD_LABELS[s.hazard_class]}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        color: SEVERITY_COLOUR[s.alert.severity as string],
                        fontWeight: 600,
                      }}
                    >
                      {s.alert.severity?.toUpperCase()}
                    </span>
                    <div style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                      {s.alert.urgency?.toUpperCase()}
                    </div>
                  </td>
                  <td className="num">{topTier(s.settlement_id) || ''}</td>
                  <td style={{ color: 'var(--ink-text-dim)', whiteSpace: 'normal', minWidth: 220 }}>
                    {(() => {
                      const roles = firstRoles(s.settlement_id)
                      const lead = roles.find((r) => /District Collector/.test(r)) ?? roles[0]
                      return lead ? (
                        <span title={roles.join(' · ')}>
                          <span style={{ color: 'var(--ink-text)' }}>{lead}</span>
                          {roles.length > 1 && <span> · +{roles.length - 1} tier-1 roles</span>}
                        </span>
                      ) : null
                    })()}
                  </td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>{hm(s.alert.lead_time_min as number)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
        {advisory.length > 0 && (
          <p
            className="mt-3"
            style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}
          >
            {advisory.map((s) => s.name).join(' and ')}{' '}
            {advisory.length > 1 ? 'carry' : 'carries'} a standing advisory: the front had not
            reached {advisory.length > 1 ? 'them' : 'it'} at the last simulated frame and the
            water was still spreading, so no arrival time or depth exists for{' '}
            {advisory.length > 1 ? 'them' : 'it'} yet.
          </p>
        )}

        {detail.length > 0 && (
          <div className="mt-3" style={{ borderTop: '1px solid var(--ink-border)' }}>
            <div className="section-label mb-1 mt-3">
              Dispatches for {rows[0].name} · {detail.length} on{' '}
              {new Set(detail.map((e) => e.channel)).size} channels to{' '}
              {new Set(detail.map((e) => e.recipient)).size} recipients
            </div>
            <div className="overflow-x-auto" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table className="data-table w-full" style={{ minWidth: 520 }}>
                <thead>
                  <tr>
                    <th className="num">clock</th>
                    <th className="num">tier</th>
                    <th>recipient</th>
                    <th>channel</th>
                    <th className="num">lead time</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.map((e) => (
                    <tr key={`${e.recipient}-${e.channel}-${e.t_notified_min}`}>
                      <td className="num">{e.exercise_clock}</td>
                      <td className="num">{e.tier}</td>
                      <td>{e.recipient}</td>
                      <td style={{ color: 'var(--ink-text-dim)' }}>{e.channel}</td>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {e.lead_time_min === null ? '' : hm(e.lead_time_min)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
